// ─── El cliente del generador de animaciones por código ──────────────────────
// `POST /v1/estudio/animacion` del backend (ADR-2026-09-24-animacion-por-codigo):
// el agente describe la pieza y un Sonnet con las reglas de Remotion escribe el
// componente, ya validado (lint + esbuild). Acá sólo se arma el pedido y se
// interpreta la respuesta; quién lo llama son las herramientas `crear_animacion`
// y `modificar_animacion` del editor.

import { atlasBackendFetch } from './atlasBackend';
import { CREDITOS_ANIMACION } from '../animations/codigo/reglas';
import type { AssetDeAnimacion, MarcaDeAnimacion, TipoDeAnimacionPorCodigo } from '../animations/codigo/versiones';

export type PedidoDeAnimacion = {
  brief: string;
  tipo: TipoDeAnimacionPorCodigo;
  formato: '16:9' | '9:16' | '4:3';
  duracionSeg?: number;
  marca: MarcaDeAnimacion;
  assets: AssetDeAnimacion[];
  /** Los dos juntos, sólo al modificar. */
  codigoActual?: string;
  cambios?: string;
};

export type RespuestaDeAnimacion =
  | { ok: true; codigo: string; resumen: string; duracionSeg: number }
  | { ok: false; codigo: 'no-compila' | 'sin-saldo' | 'error'; mensaje: string };

/** El generador tarda 10 a 50 s por llamada (el backend corta a 150 s); con la vuelta de
 *  reparación puede pasar los dos minutos. Un poco más que el backend, para que el error
 *  que llegue sea el suyo (con motivo) y no un abort mudo de acá. */
const TIMEOUT_MS = 180_000;

export async function pedirAnimacionPorCodigo(p: PedidoDeAnimacion): Promise<RespuestaDeAnimacion> {
  let res: Response;
  try {
    res = await atlasBackendFetch(
      '/v1/estudio/animacion',
      {
        brief: p.brief,
        tipo: p.tipo,
        formato: p.formato,
        ...(p.duracionSeg ? { duracionSeg: p.duracionSeg } : {}),
        marca: p.marca,
        // Al backend van sólo url y descripción; el storagePath es del editor.
        assets: p.assets.map((a) => ({ url: a.url, descripcion: a.descripcion })),
        ...(p.codigoActual && p.cambios ? { codigoActual: p.codigoActual, cambios: p.cambios } : {}),
      },
      { signal: AbortSignal.timeout(TIMEOUT_MS) },
    );
  } catch (err) {
    return {
      ok: false,
      codigo: 'error',
      mensaje:
        err instanceof Error && err.name === 'TimeoutError'
          ? 'El generador tardó demasiado. Probá con un pedido más simple.'
          : 'No se pudo hablar con el generador de animaciones.',
    };
  }
  const json = (await res.json().catch(() => null)) as
    | {
        response?: string;
        codigo?: string;
        resumen?: string;
        duracionSeg?: number;
        code?: string;
        error?: string;
        error_type?: string;
      }
    | null;
  if (res.ok && json?.response === 'success' && typeof json.codigo === 'string') {
    return {
      ok: true,
      codigo: json.codigo,
      resumen: typeof json.resumen === 'string' ? json.resumen : '',
      duracionSeg: typeof json.duracionSeg === 'number' ? json.duracionSeg : 4,
    };
  }
  if (res.status === 422 && json?.code === 'no-compila') {
    return { ok: false, codigo: 'no-compila', mensaje: json.error ?? 'El código generado no compiló.' };
  }
  if (res.status === 402) {
    const costo = p.codigoActual ? CREDITOS_ANIMACION.modificar : CREDITOS_ANIMACION.crear;
    return {
      ok: false,
      codigo: 'sin-saldo',
      mensaje: `No te quedan créditos suficientes: ${p.codigoActual ? 'cambiar' : 'crear'} una animación cuesta ${costo}.`,
    };
  }
  if (res.status === 504 || json?.error_type === 'provider_timeout') {
    return {
      ok: false,
      codigo: 'error',
      mensaje:
        'El generador tardó demasiado y se cortó. Probá con un pedido más corto o más simple (menos elementos, menos pasos).',
    };
  }
  return {
    ok: false,
    codigo: 'error',
    mensaje: `El generador respondió ${res.status}. Probá de nuevo en un momento.`,
  };
}
